import {
  Home,
  Users,
  DollarSign,
  LogOut,
  FileText,
  Menu,
  Package,
  Percent,
  Building2,
  ChevronDown,
  MessageSquare,
} from 'lucide-react';
import React, { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../constants/providers/AuthProvider';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useSidebar } from '../../context/SidebarContext';
import { supabase } from '../../lib/supabase';
import { NotificationBell } from '../notifications/NotificationBell';

interface TopBarItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  isAvailable?: boolean;
}

interface UserCompany {
  company_id: string;
  company_name: string;
  company_logo?: string | null;
  role: string;
}

const topBarItems: TopBarItem[] = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: <Home className="w-4 h-4" />,
    isAvailable: true,
  },
  {
    path: '/students',
    label: 'Students',
    icon: <Users className="w-4 h-4" />,
    isAvailable: true,
  },
  {
    path: '/damage-waivers',
    label: 'Documents',
    icon: <FileText className="w-4 h-4" />,
    isAvailable: true,
  },
  {
    path: '/social',
    label: 'Social',
    icon: <MessageSquare className="w-4 h-4" />,
    isAvailable: true,
  },
  {
    path: '/subscriptions',
    label: 'Subscriptions',
    icon: <Package className="w-4 h-4" />,
    isAvailable: true,
  },
  {
    path: '/billing',
    label: 'Billing',
    icon: <DollarSign className="w-4 h-4" />,
    isAvailable: true,
  },
];

export const TopBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const { toggle } = useSidebar();
  const { allCompanies, company, role, isLoading } = useCurrentCompany();
  const companies = useMemo<UserCompany[]>(
    () => allCompanies.map((item) => ({
      company_id: item.company_id,
      company_name: item.company?.name || 'Unknown Gym',
      company_logo: item.company?.logo_url,
      role: item.role || 'Member',
    })),
    [allCompanies],
  );
  const currentCompany = useMemo<UserCompany | null>(() => {
    if (!company) {
      return null;
    }
    return {
      company_id: company.id,
      company_name: company.name || 'Unknown Gym',
      company_logo: company.logo_url,
      role: role || 'Member',
    };
  }, [company, role]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  const handleSwitchCompany = async (company: UserCompany) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Update last_selected_company
      await supabase
        .from('user_profiles')
        .update({ last_selected_company: company.company_id })
        .eq('id', user.id);

      setShowCompanyDropdown(false);

      // Refresh JWT to get new company_id claim
      await refreshSession();

      // Refresh the page to load new company data
      window.location.reload();
    } catch (err) {
      console.error('Error switching company:', err);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 h-14 bg-[#221F1F] border-b border-[#504A4A] z-50">
      <div className="flex items-center h-full px-4 gap-2">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
          <img 
            src="/rollops-logo.png" 
            alt="RollOps Logo" 
            width={24} 
            height={24} 
            className="w-6 h-6" 
          />
          <span className="text-lg font-bold text-[#EBFFEE]">
            Roll<span className="text-rollops-accent">O</span>ps
          </span>
        </Link>

        {/* Company Selector */}
        {!isLoading && currentCompany && (
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
              className="flex items-center gap-2 px-2 py-1 rounded-lg bg-[#363232] hover:bg-[#504A4A] transition-colors"
            >
              {currentCompany.company_logo ? (
                <img
                  src={currentCompany.company_logo}
                  alt={currentCompany.company_name}
                  className="w-6 h-6 rounded-full object-cover"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-[#9B1421] flex items-center justify-center">
                  <Building2 className="w-3 h-3 text-white" />
                </div>
              )}
              <span className="text-xs font-medium text-[#EBFFEE] max-w-[100px] truncate">
                {currentCompany.company_name}
              </span>
              <ChevronDown className={`w-3 h-3 text-[#A9A3A3] transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            {showCompanyDropdown && (
              <div className="absolute top-full left-0 mt-1 bg-[#363232] border border-[#504A4A] rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto scrollbar-styled min-w-[200px]">
                {companies.map((company) => (
                  <button
                    key={company.company_id}
                    onClick={() => handleSwitchCompany(company)}
                    className={`w-full flex items-center gap-2 p-2 hover:bg-[#504A4A] transition-colors ${
                      currentCompany?.company_id === company.company_id ? 'bg-[#504A4A]' : ''
                    }`}
                  >
                    {company.company_logo ? (
                      <img
                        src={company.company_logo}
                        alt={company.company_name}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-[#9B1421] flex items-center justify-center">
                        <Building2 className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-xs font-medium text-[#EBFFEE] truncate">
                        {company.company_name}
                      </p>
                      <p className="text-xs text-[#A9A3A3] truncate">
                        {company.role}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Navigation Items - Scrollable */}
        <div className="flex-1 overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-1 min-w-max">
            {topBarItems
              .filter(item => item.isAvailable)
              .map((item) => {
                const isActive = location.pathname === item.path || 
                  (item.path === '/billing' ? location.pathname.startsWith('/billing') : location.pathname.startsWith(item.path + '/'));
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      flex items-center px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap
                      ${isActive 
                        ? 'bg-[#9B1421] text-[#EBFFEE]' 
                        : 'text-[#A9A3A3] hover:bg-[#363232] hover:text-[#EBFFEE]'
                      }
                    `}
                  >
                    <span className="flex-shrink-0" style={{ marginRight: '8px' }}>{item.icon}</span>
                    <span className="text-xs font-medium">{item.label}</span>
                  </Link>
                );
              })}
          </div>
        </div>

        {/* Menu Toggle / Notifications / Sign Out */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <NotificationBell />
          <button
            onClick={toggle}
            className="w-8 h-8 flex items-center justify-center text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded-lg transition-colors"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-4 h-4" />
          </button>
          <button
            onClick={handleSignOut}
            className="w-8 h-8 flex items-center justify-center text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded-lg transition-colors"
            aria-label="Sign out"
            title="Sign Out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </nav>
  );
};
